import { CloseOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Select } from "antd";
import { useScenarioSelectOptions } from "components/UI/Selects/ScenarioSelectSearchable/useScenarioSelectOptions";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";

import FilterRequest = App.Http.Requests.FilterRequest;
import ScenarioOrderItemRequest = App.Http.Requests.FormField.ScenarioOrderItemRequest;

interface OrderableScenarioSelectProps {
    value?: ScenarioOrderItemRequest[];
    onChange?: (value: ScenarioOrderItemRequest[]) => void;
    placeholder?: string;
    disabled?: boolean;
    filter?: FilterRequest;
    formStructureId?: string;
}

export const OrderableScenarioSelect = ({
    value = [],
    onChange,
    placeholder,
    disabled,
    filter,
    formStructureId,
}: OrderableScenarioSelectProps) => {
    const { t } = usePaldockTranslation("forms.general-fields.scenarios");
    const { options, loading } = useScenarioSelectOptions({ filter, formStructureId });

    const getLabelForId = (id: string) => options.find((opt) => opt.value === id)?.label ?? id;

    const selectedIds = value.map((item) => item.id);
    const availableOptions = options.filter((opt) => !selectedIds.includes(String(opt.value)));

    const handleAdd = (id: string) => {
        const newItem: ScenarioOrderItemRequest = { id, order: value.length };
        onChange?.([...value, newItem]);
    };

    const handleRemove = (id: string) => {
        const next = value.filter((item) => item.id !== id).map((item, index) => ({ ...item, order: index }));
        onChange?.(next);
    };

    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        const next = [...value];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        onChange?.(next.map((item, i) => ({ ...item, order: i })));
    };

    const handleMoveDown = (index: number) => {
        if (index === value.length - 1) return;
        const next = [...value];
        [next[index + 1], next[index]] = [next[index], next[index + 1]];
        onChange?.(next.map((item, i) => ({ ...item, order: i })));
    };

    return (
        <div>
            {value.length > 0 && !loading && (
                <div className="mb-2 flex flex-col gap-1">
                    {value.map((item, index) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-1 rounded border border-solid border-gray-200 bg-gray-50 px-2 py-1"
                        >
                            <span className="mr-1 min-w-0 flex-1 truncate text-sm">{getLabelForId(item.id)}</span>
                            <Button
                                type="text"
                                size="small"
                                icon={<UpOutlined />}
                                disabled={index === 0}
                                onClick={() => handleMoveUp(index)}
                            />
                            <Button
                                type="text"
                                size="small"
                                icon={<DownOutlined />}
                                disabled={index === value.length - 1}
                                onClick={() => handleMoveDown(index)}
                            />
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => handleRemove(item.id)}
                            />
                        </div>
                    ))}
                </div>
            )}
            <Select
                showSearch
                style={{ width: "100%" }}
                placeholder={placeholder ?? t("placeholder")}
                options={availableOptions}
                loading={loading}
                disabled={disabled || loading}
                value={null}
                filterOption={(input, option) =>
                    (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(id) => handleAdd(String(id))}
            />
        </div>
    );
};
